import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import './landing.css';

/**
 * Concept 4 — NUMBERS.
 * Stripe-homepage-style math-confident landing. Gigantic numeric facts
 * dominate the page. Each section is a tile with a single stat that
 * earns the rest of the copy. Geometric Sora font, electric-blue +
 * chartreuse accent palette.
 */
export default function Landing4() {
  return (
    <div className="lp4 lp-shell" data-testid="landing-4">
      {/* LandingSwitcher removed Round 91l. */}

      <header className="max-w-6xl mx-auto px-6 pt-32 pb-20">
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 font-bold mb-8">
          The dividend tracker for the math-confident.
        </div>
        <h1 className="mega-num leading-none" data-testid="lp4-title">
          4,<span className="accent">598</span>
        </h1>
        <div className="mt-6 text-2xl text-zinc-700 max-w-xl leading-tight">
          Dividend-paying tickers tracked, verified, and locked against bad data.
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/" className="lp-btn lp-btn-primary-4" data-testid="lp4-cta-primary">
            Start tracking <ArrowRight className="w-4 h-4" />
          </Link>
          <a href="/screener" className="lp-btn border border-zinc-300 text-zinc-700">
            Open the screener
          </a>
        </div>
      </header>

      {/* Stat wall */}
      <section className="border-t border-zinc-200 grid grid-cols-1 md:grid-cols-3">
        <div className="stat-tile border-r border-b border-zinc-200">
          <div className="tag text-zinc-500 mb-3">EXCHANGE-VERIFIED</div>
          <div className="stat-num-mini">99.7<span className="text-2xl">%</span></div>
          <div className="mt-3 text-sm text-zinc-600 leading-relaxed">of dividend-paying tickers in our DB have pay dates pulled directly from Massive&apos;s exchange feed and locked against estimator overwrites.</div>
        </div>
        <div className="stat-tile border-r border-b border-zinc-200">
          <div className="tag text-zinc-500 mb-3">LIVE LATENCY</div>
          <div className="stat-num-mini">&lt;50<span className="text-2xl">ms</span></div>
          <div className="mt-3 text-sm text-zinc-600 leading-relaxed">from market tick to your portfolio summary card. WebSocket streaming with debounced background refresh — no page jumps.</div>
        </div>
        <div className="stat-tile border-b border-zinc-200">
          <div className="tag text-zinc-500 mb-3">PRICE</div>
          <div className="stat-num-mini">$0</div>
          <div className="mt-3 text-sm text-zinc-600 leading-relaxed">No subscription. No ads. No upsell. Free forever for the dividend investors who already pay enough fees elsewhere.</div>
        </div>

        <div className="stat-tile border-r border-b border-zinc-200">
          <div className="tag text-zinc-500 mb-3">YEARS OF HISTORY</div>
          <div className="stat-num-mini">3</div>
          <div className="mt-3 text-sm text-zinc-600 leading-relaxed">of monthly yield-history charts per ticker. Crossover detection, log-scale toggle, side-by-side overlay — all built in.</div>
        </div>
        <div className="stat-tile border-r border-b border-zinc-200">
          <div className="tag text-zinc-500 mb-3">PINS PER USER</div>
          <div className="stat-num-mini">15</div>
          <div className="mt-3 text-sm text-zinc-600 leading-relaxed">favorite Compare pairs you can star permanently. Each one carries a memo so you remember why it mattered.</div>
        </div>
        <div className="stat-tile border-b border-zinc-200">
          <div className="tag text-zinc-500 mb-3">CACHE FIELDS LOCKED</div>
          <div className="stat-num-mini">52</div>
          <div className="mt-3 text-sm text-zinc-600 leading-relaxed">stock metadata fields protected against "real → null" overwrites. Your CAGR, your tier badge, your perf cells stay stable.</div>
        </div>

        <div className="stat-tile border-r border-b border-zinc-200">
          <div className="tag text-zinc-500 mb-3">TIERS</div>
          <div className="stat-num-mini">5</div>
          <div className="mt-3 text-sm text-zinc-600 leading-relaxed">Kings / Aristocrats / Achievers / Contenders / Challengers. Auto-classified from streak data, queryable in screener.</div>
        </div>
        <div className="stat-tile border-r border-b border-zinc-200">
          <div className="tag text-zinc-500 mb-3">CALCULATORS</div>
          <div className="stat-num-mini">13</div>
          <div className="mt-3 text-sm text-zinc-600 leading-relaxed">DRIP, taxes, FIRE date, sector rebalancer, what-if-$1k-ago, dividend per pay period, all in one Tool Box.</div>
        </div>
        <div className="stat-tile border-b border-zinc-200">
          <div className="tag text-zinc-500 mb-3">SOURCE OF TRUTH</div>
          <div className="stat-num-mini">1</div>
          <div className="mt-3 text-sm text-zinc-600 leading-relaxed">Massive&apos;s exchange feed. We don&apos;t guess pay dates. We don&apos;t synthesize ex-div dates from the last cycle. We pull, we lock, we display.</div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-32">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="tag text-zinc-500 mb-4">WHAT WE DON&apos;T DO</div>
            <h2 className="text-5xl font-bold tracking-tight leading-tight mb-6">
              No fluff.<br />Just dividends.
            </h2>
            <p className="text-zinc-600 leading-relaxed text-lg">
              No options chains we don&apos;t need. No alt-coin sidebar. No "social trading" feed.
              Just the four things that matter for income investors:
              <span className="font-bold text-zinc-900"> price, yield, ex-div date, pay date.</span> Done right.
            </p>
          </div>
          <div className="space-y-2 font-mono text-xs">
            {[
              ['price', '✓', 'sky'],
              ['yield', '✓', 'sky'],
              ['ex-div date', '✓ locked', 'lime'],
              ['pay date', '✓ locked', 'lime'],
              ['options chains', '✗', 'zinc'],
              ['crypto feed', '✗', 'zinc'],
              ['social signals', '✗', 'zinc'],
              ['paid newsletter upsell', '✗', 'zinc'],
              ['ads', '✗', 'zinc'],
            ].map(([k, v, tone]) => (
              <div key={k} className={`flex items-center justify-between py-2 px-3 border-l-4 ${tone === 'lime' ? 'border-lime-500 bg-lime-50' : tone === 'sky' ? 'border-sky-500 bg-sky-50' : 'border-zinc-300 bg-zinc-50'}`}>
                <span className="text-zinc-700">{k}</span>
                <span className={tone === 'lime' ? 'text-lime-700 font-bold' : tone === 'sky' ? 'text-sky-700 font-bold' : 'text-zinc-400'}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-200 py-12 text-center text-xs text-zinc-500">
        © 2026 · DiviDrip
      </footer>
    </div>
  );
}
